import React, {useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { GetPublicBuildingApartmentDetails } from '../../../reduxToolkit/Building/BuildingApi';
import {MY_APARTMENT_DETAIL_MODEL} from "../Model/Model";
import {calculateHeight, errorMsg, isSmWidth, tryGetAddress} from '../../../Apis/functions';
/**
 * Hook component for managing building details.
 * Handles fetching and updating of the building data and its related invitations.
 *
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 */
const MyApartmentDetailsApp = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();
    // Apartment of the card that opened the detail, it carries the
    // buildingApartmentId the detail and the household members are searched with
    const { state } = useLocation();
    const buildingApartmentId = state?.buildingApartmentId ?? state?.id ?? id;
    // Apartment shown by the information card, the fields of the card of the
    // list are kept until the detail endpoint answers
    const [buildingDetail, setBuildingDetail] = useState({
        ...MY_APARTMENT_DETAIL_MODEL,
        ...(state || {})
    });
    const containerRef = useRef(null);
    const [containerHeight, setContainerHeight] = useState('auto');

    useEffect(() => {
        if (isSmWidth()) return;

        if (containerRef.current) {
            calculateHeight({
                elementRef: containerRef,
                bottom: 50,
                setHeight:setContainerHeight
            });
        }
    }, []);

    /**
     * Loads the apartment rendered by the information card.
     *
     * Side effects:
     * - Loads the apartment of the information card
     */
    useEffect(() => {
        getApartmentDetail();
    }, []);

    /**
     * Resolves the address of the building out of its coordinates, the detail
     * endpoint only returns the position.
     *
     * @effect
     */
    useEffect(() => {
        const {latitude, longitude} = buildingDetail;
        if (!latitude || !longitude) return;

        setBuildingDetail((prev) => ({...prev, location: t('loading')}));
        tryGetAddress(latitude, longitude, dispatch, (address) => {
            setBuildingDetail((prev) => ({...prev, location: address}));
        });
    }, [buildingDetail.latitude, buildingDetail.longitude]);

    /**
     * Loads the apartment rendered by the information card, the building, the
     * unit, its coordinates and the contact data of the main resident.
     *
     * @function getApartmentDetail
     */
    const getApartmentDetail = () => {
        if (!buildingApartmentId) return;

        dispatch(GetPublicBuildingApartmentDetails(buildingApartmentId)).then((response) => {
            const {success, data, code} = response?.payload?.data || {};
            if (!success) {
                errorMsg(code);
                return;
            }
            setBuildingDetail((prev) => ({...prev, ...MY_APARTMENT_DETAIL_MODEL.set(data)}));
        });
    };

    return {
        t,
        buildingDetail,
        containerRef,
        containerHeight
    };
};

export default MyApartmentDetailsApp;
